// Signals a 401 from the exchange or bug-tracker API -- the caller's session is stale and a fresh sign-in is needed.
export const BUG_TRACKER_AUTH_EXPIRED = 'bug-tracker-auth-expired';

export type ExchangeBugTrackerTokenParams = {
	keystoneUrl: string;
	subjectToken: string;
};

type TokenExchangeResponse = {
	token: string;
	expiresAt: string;
};

// RFC 8693 on-behalf-of exchange -- re-audiences the blueprint session token to the observability API without a second sign-in.
export const exchangeBugTrackerToken = async (params: ExchangeBugTrackerTokenParams): Promise<[string | null, string | null]> => {
	const { keystoneUrl, subjectToken } = params;

	// An unset audience is dropped by JSON.stringify, and Keystone answers with a bare
	// "audience: Required" 422 that says nothing about which side is misconfigured.
	const audience = import.meta.env.VITE_BUG_TRACKER_TOKEN_AUDIENCE;
	if (!audience) return [null, 'VITE_BUG_TRACKER_TOKEN_AUDIENCE is not set — bug reporting cannot authorize'];

	try {
		const response = await fetch(`${keystoneUrl}/api/v1/auth/token-exchange`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			// Field names are the RFC 8693 wire contract (snake_case), not JS identifiers.
			body: JSON.stringify({
				'subject_token': subjectToken,
				'subject_token_type': 'urn:ietf:params:oauth:token-type:jwt',
				audience
			})
		});

		if (!response.ok) {
			const errorText = await response.text();
			// Keystone answers 401 for TWO unrelated things: TOKEN_INVALID (a genuinely stale
			// session, which signing in again fixes) and CALLER_UNKNOWN (this app is not a
			// registered caller, which it never fixes). Only the first earns the re-login prompt —
			// showing it for the second sends the reporter round a loop that cannot terminate.
			if (response.status === 401 && errorText.includes('TOKEN_INVALID')) return [null, BUG_TRACKER_AUTH_EXPIRED];
			// Echo the audience we asked for: the rest are mismatches between this string and what
			// Keystone has registered, and the response alone never says which.
			return [null, `Bug-tracker token exchange failed: ${response.status} ${errorText} (requested audience: ${audience})`];
		}

		const data: TokenExchangeResponse = await response.json();
		return [data.token, null];
	} catch (error) {
		console.warn('Error exchanging bug-tracker token', error);
		return [null, `Error exchanging bug-tracker token: ${(error as Error).message}`];
	}
};
